import { Controller } from "@hotwired/stimulus"

const INDICATOR_ID = 'pull-to-refresh-indicator'
const SPINNER_HEIGHT = 60
const CONTENT_OFFSET_MAX = 40
const DEFAULT_FRAME = 'content'
const TRANSITION_DURATION = '0.5s'
const TRANSITION_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
const HIDE_DELAY = 400
const REFRESH_DELAY = 500
const PULL_RESISTANCE = 0.5 // Higher = more resistance (0-1)
const MIN_PULL_DISTANCE = 30 // Small threshold before drag starts (prevents accidental triggers)

export default class extends Controller {
    static targets = ["bottomSheetContainer", "indicator"]

    static values = {
        threshold: { type: Number, default: 180 },
        disabled: { type: Boolean, default: false },
        path: { type: String, default: "" },
        frame: { type: String, default: DEFAULT_FRAME },
        target: { type: String, default: "" }
    }

    connect() {
        this.startY = 0
        this.currentY = 0
        this.isRefreshing = false
        this.isZooming = false
        this.hasVibratedThisPull = false
        this.initialTouchDistance = 0
        this.timeoutIds = []
        this.indicator = null
        this.contentFrame = null
        this.scrollableContent = null
        this.gradientDiv = null

        this.boundHandleTouchStart = this.handleTouchStart.bind(this)
        this.boundHandleTouchMove = this.handleTouchMove.bind(this)
        this.boundHandleTouchEnd = this.handleTouchEnd.bind(this)
        this.boundHandleFrameLoad = this.handleFrameLoad.bind(this)

        document.addEventListener('touchstart', this.boundHandleTouchStart, { passive: true })
        document.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false })
        document.addEventListener('touchend', this.boundHandleTouchEnd, { passive: true })
        document.addEventListener('turbo:frame-load', this.boundHandleFrameLoad)
    }

    handleFrameLoad() {
        this.resetCache()
    }

    resetCache() {
        this.contentFrame = null
        this.scrollableContent = null
        this.gradientDiv = null
    }

    disconnect() {
        document.removeEventListener('touchstart', this.boundHandleTouchStart)
        document.removeEventListener('touchmove', this.boundHandleTouchMove)
        document.removeEventListener('touchend', this.boundHandleTouchEnd)
        document.removeEventListener('turbo:frame-load', this.boundHandleFrameLoad)
        this.clearAllTimeouts()
        this.hideIndicator()
        this.resetContentPosition()
        this.resetCache()
    }

    clearAllTimeouts() {
        this.timeoutIds.forEach(id => clearTimeout(id))
        this.timeoutIds = []
    }

    setManagedTimeout(callback, delay) {
        const id = setTimeout(() => {
            callback()
            this.timeoutIds = this.timeoutIds.filter(timeoutId => timeoutId !== id)
        }, delay)
        this.timeoutIds.push(id)
        return id
    }

    handleTouchStart(event) {
        // Detect multi-touch (pinch-to-zoom)
        if (event.touches.length > 1) {
            this.isZooming = true
            this.initialTouchDistance = this.calculateTouchDistance(event.touches)
            return
        }

        if (!this.canStartPull(event)) {
            return
        }
        this.startY = event.touches[0].clientY
    }

    calculateTouchDistance(touches) {
        if (touches.length < 2) {
            return 0
        }
        const dx = touches[0].clientX - touches[1].clientX
        const dy = touches[0].clientY - touches[1].clientY
        return Math.hypot(dx * dx + dy * dy)
    }

    handleTouchMove(event) {
        // Detect multi-touch (zoom gesture)
        if (event.touches.length > 1) {
            this.isZooming = true
            return
        }

        // Don't pull-to-refresh during or after zoom
        if (this.isZooming) {
            return
        }

        if (!this.startY || this.isRefreshing || !this.hasValidTouch(event)) {
            return
        }

        // Check if pull-to-refresh should be disabled
        if (this.isBottomSheetOpen() || this.canScrollUp()) {
            return
        }

        this.currentY = event.touches[0].clientY
        const pullDistance = this.currentY - this.startY

        // Start smooth drag after small threshold (30px) to prevent accidental triggers
        if (pullDistance > MIN_PULL_DISTANCE && this.isAtTop()) {
            event.preventDefault()
            const progress = this.calculateProgress(pullDistance)
            this.updateIndicator(progress, pullDistance)
            this.moveContentDown(pullDistance)

            // Vibrate when threshold reached (haptic feedback)
            if (pullDistance >= this.thresholdValue && !this.hasVibratedThisPull) {
                // Try vibration (works on Android)
                if (navigator.vibrate) {
                    navigator.vibrate(50)
                }

                this.hasVibratedThisPull = true
            }
        } else if (pullDistance > 0 && this.isAtTop()) {
            // Allow pull but don't show indicator until minimum distance
            event.preventDefault()
        }
    }

    handleTouchEnd() {
        if (!this.startY) {
            this.resetTouchState()
            return
        }

        const pullDistance = this.currentY - this.startY

        if (pullDistance >= this.thresholdValue && !this.isRefreshing && !this.isZooming) {
            this.refresh()
        } else {
            this.hideIndicator()
            this.resetContentPosition()
        }

        this.resetTouchState()
    }

    canStartPull(event) {
        return this.isEnabled() &&
               this.isAtTop() &&
               !this.isRefreshing &&
               this.hasValidTouch(event) &&
               !this.isBottomSheetOpen() &&
               !this.canScrollUp()
    }

    isAtTop() {
        const scrollableContent = this.getScrollableContent()
        if (scrollableContent) {
            // Check if scrollable content is at the top
            return scrollableContent.scrollTop === 0
        }
        // Fallback to window scroll position
        return globalThis.scrollY === 0
    }

    hasValidTouch(event) {
        return event.touches && event.touches.length > 0
    }

    calculateProgress(distance) {
        return Math.min(distance / this.thresholdValue, 1)
    }

    resetTouchState() {
        this.startY = 0
        this.currentY = 0
        this.isZooming = false
        this.hasVibratedThisPull = false
        this.initialTouchDistance = 0
    }

    getIndicator() {
        // Use Stimulus target if available
        if (this.hasIndicatorTarget) {
            return this.indicatorTarget
        }

        // Fallback: cache and return if already found
        if (this.indicator && this.indicator.isConnected) {
            return this.indicator
        }

        const contentFrame = this.getContentFrame()
        if (!contentFrame) {
            return null
        }

        // Fallback: query for the indicator element by ID
        this.indicator = contentFrame.querySelector(`#${INDICATOR_ID}`)
        return this.indicator
    }

    updateIndicator(progress, distance) {
        const indicator = this.getIndicator()
        if (!indicator) {
            return
        }

        const resistedDistance = this.applyPullResistance(distance)
        const progressRatio = this.normalizeDistance(resistedDistance)

        const atOrPastThreshold = progress >= 1

        const rotation = atOrPastThreshold ? 360 : progress * 360
        const scale = atOrPastThreshold ? 1 : 0.8 + (progress * 0.2)
        const opacity = atOrPastThreshold ? 1 : Math.min(progressRatio * 1.5, 1)

        const easedProgress = 1 - Math.pow(1 - Math.min(progressRatio, 1), 2)
        const translateY = atOrPastThreshold ? 0 : (-SPINNER_HEIGHT + (easedProgress * SPINNER_HEIGHT))

        this.updateSpinnerContent(indicator, rotation, scale, progress, atOrPastThreshold)
        indicator.style.opacity = opacity.toString()
        indicator.style.transform = `translateY(${translateY}px)`
        indicator.style.transition = 'none'
    }

    updateSpinnerContent(indicator, rotation, scale, progress, atOrPastThreshold) {
        if (!indicator.querySelector('svg')) {
            indicator.innerHTML = this.buildSpinnerHTML(rotation, scale)
        }
        const svg = indicator.querySelector('svg')
        const container = indicator.querySelector('div')
        if (svg) {
            svg.classList.remove('animate-spin')
            svg.style.animation = ''
            svg.style.transform = `rotate(${rotation}deg)`
        }
        if (container) {
            container.style.transform = `scale(${scale})`
            const minOpacity = 0.2
            const maxOpacity = 0.7
            const backdropOpacity = atOrPastThreshold ? maxOpacity : (minOpacity + (progress * (maxOpacity - minOpacity)))
            container.style.backgroundColor = `rgba(255, 255, 255, ${backdropOpacity})`
        }
    }

    buildSpinnerHTML(rotation, scale) {
        return `
            <div class="bg-white/20 backdrop-blur-sm rounded-full p-3"
                 style="transform: scale(${scale});">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"
                     style="transform: rotate(${rotation}deg); transition: transform 0.15s ease-out;">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8M21 8v-5M21 8h-5M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16M21 16v5M21 16h-5"/>
                </svg>
            </div>
        `
    }

    normalizeDistance(distance) {
        const normalized = Math.min(Math.max(distance, 0), this.thresholdValue) / this.thresholdValue
        // Apply smooth easing with resistance; clamp output to [0, 1]
        const eased = 1 - Math.pow(1 - normalized, 3)
        return Math.max(0, Math.min(1, eased))
    }

    applyPullResistance(distance) {
        const maxDistance = this.thresholdValue * 1.5
        if (distance <= this.thresholdValue) {
            return distance
        }
        const excess = distance - this.thresholdValue
        const range = maxDistance - this.thresholdValue
        const resistance = Math.max(0, 1 - (excess / range) * PULL_RESISTANCE)
        const resisted = this.thresholdValue + (excess * resistance)
        return Math.min(Math.max(resisted, 0), maxDistance)
    }

    hideIndicator() {
        const indicator = this.getIndicator()
        if (!indicator) {
            return
        }

        // Smooth fade out and slide up
        indicator.style.transition = `opacity 0.5s ${TRANSITION_EASING}, transform 0.5s ${TRANSITION_EASING}`
        indicator.style.opacity = '0'
        indicator.style.transform = `translateY(-${SPINNER_HEIGHT}px)`

        // Reset the spinner animation
        const svg = indicator.querySelector('svg')
        if (svg) {
            svg.classList.remove('animate-spin')
            svg.style.animation = ''
        }

        const container = indicator.querySelector('div')
        if (container) {
            container.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
        }
    }

    getContentFrame() {
        if (this.contentFrame && this.contentFrame.isConnected) {
            return this.contentFrame
        }

        if (this.hasTargetValue && this.targetValue) {
            // Prefer this.element.querySelector over document.querySelector (Stimulus best practice)
            this.contentFrame = this.element.querySelector(this.targetValue)
            if (this.contentFrame) {
                return this.contentFrame
            }
        }

        const frameId = this.frameValue || DEFAULT_FRAME
        // Prefer this.element.querySelector and getElementById over document.querySelector
        this.contentFrame = this.element.querySelector(`#${frameId}`) ||
                           this.element.querySelector(`turbo-frame#${frameId}`) ||
                           document.getElementById(frameId) ||
                           this.element.querySelector('turbo-frame:not([src=""])')

        return this.contentFrame
    }

    getScrollableContent() {
        if (this.scrollableContent && this.scrollableContent.isConnected) {
            return this.scrollableContent
        }

        const contentFrame = this.getContentFrame()
        if (!contentFrame) {
            return null
        }

        // Find the scrollable section (waas-modal) inside the frame
        this.scrollableContent = contentFrame.querySelector('#waas-modal.waas-modal') ||
            contentFrame.querySelector('dialog.waas-modal') ||
            contentFrame.querySelector('.waas-modal')

        return this.scrollableContent
    }

    getGradientDiv() {
        if (this.gradientDiv && this.gradientDiv.isConnected) {
            return this.gradientDiv
        }

        const contentFrame = this.getContentFrame()
        if (contentFrame) {
            this.gradientDiv = contentFrame.querySelector('.bg-wallet-gradient')
        }
        return this.gradientDiv
    }

    getRefreshPath() {
        return (this.hasPathValue && this.pathValue) ? this.pathValue : (globalThis.location?.pathname || '')
    }

    getRefreshFrame() {
        return this.frameValue || DEFAULT_FRAME
    }

    moveContentDown(distance) {
        const scrollableContent = this.getScrollableContent()
        if (!scrollableContent) {
            return
        }

        // Apply progressive resistance to make pull feel natural
        const resistedDistance = this.applyPullResistance(distance)
        const progressRatio = this.normalizeDistance(resistedDistance)

        // Use smooth easing curve for content offset; clamp so we never translate off-screen
        const easedProgress = Math.max(0, Math.min(1, 1 - Math.pow(1 - progressRatio, 2)))
        const contentOffset = Math.max(0, Math.min(CONTENT_OFFSET_MAX, easedProgress * CONTENT_OFFSET_MAX))

        // Move only the scrollable content, not the frame
        scrollableContent.style.transform = `translateY(${contentOffset}px)`
        scrollableContent.style.transition = 'none'
    }


    resetContentPosition() {
        const scrollableContent = this.getScrollableContent()
        if (!scrollableContent) {
            return
        }

        // Smooth transition back
        const transition = `transform ${TRANSITION_DURATION} ${TRANSITION_EASING}`
        scrollableContent.style.transition = transition
        scrollableContent.style.transform = 'translateY(0)'

        this.setManagedTimeout(() => {
            if (scrollableContent) {
                scrollableContent.style.transition = ''
            }
        }, 500)
    }

    async refresh() {
        if (!this.isEnabled()) {
            return
        }

        this.isRefreshing = true
        // Do not call showSpinner() — indicator is already visible and spinning

        const refreshPath = this.getRefreshPath()
        const refreshFrame = this.getRefreshFrame()

        if (refreshPath && globalThis.Turbo) {
            globalThis.Turbo.visit(refreshPath, { frame: refreshFrame, action: 'replace' })
        }

        // Hide indicator first, then reset content, then allow new pulls
        this.setManagedTimeout(() => {
            this.hideIndicator()
            this.setManagedTimeout(() => {
                this.resetContentPosition()
                this.isRefreshing = false
            }, HIDE_DELAY)
        }, REFRESH_DELAY)
    }

    showSpinner() {
        const indicator = this.getIndicator()
        if (!indicator) {
            return
        }

        const svg = indicator.querySelector('svg')
        if (svg) {
            svg.classList.add('animate-spin')
            // Smooth spin animation - slower
            svg.style.animation = 'spin 1.2s linear infinite'
            svg.style.transition = 'transform 0.3s ease-out'
        }

        // Smooth transition to visible state
        indicator.style.transition = `opacity ${TRANSITION_DURATION} ${TRANSITION_EASING}, transform ${TRANSITION_DURATION} ${TRANSITION_EASING}`
        indicator.style.opacity = '1'
        indicator.style.transform = 'translateY(0)'
    }

    isEnabled() {
        return !this.disabledValue
    }

    isBottomSheetOpen() {
        // Use Stimulus API to get bottom sheet controller instead of querySelector
        // Try to find bottom sheet container element (prefer target, fallback to getElementById)
        let bottomSheetElement = null

        if (this.hasBottomSheetContainerTarget) {
            bottomSheetElement = this.bottomSheetContainerTarget
        } else {
            bottomSheetElement = document.getElementById('bottomsheet-container')
        }

        if (!bottomSheetElement) {
            return false
        }

        // Get bottom sheet controller using Stimulus API
        const bottomSheetController = this.application.getControllerForElementAndIdentifier(
            bottomSheetElement,
            'bottom-sheet'
        )

        if (!bottomSheetController) {
            return false
        }

        // Check if bottom sheet has sheet target and is visible
        if (!bottomSheetController.hasSheetTarget) {
            return false
        }

        const sheet = bottomSheetController.sheetTarget
        const style = globalThis.getComputedStyle(sheet)

        // Check if bottom sheet is actually visible (not hidden by display:none or opacity:0)
        return style.display !== 'none' &&
               style.opacity !== '0' &&
               style.visibility !== 'hidden' &&
               bottomSheetElement.children.length > 0
    }

    canScrollUp() {
        const scrollableContent = this.getScrollableContent()
        if (scrollableContent) {
            // If scrollable content can scroll up (scrollTop > 0), disable pull-to-refresh
            return scrollableContent.scrollTop > 0
        }

        // Fallback: check window scroll
        return globalThis.scrollY > 0
    }
}